/**
 * Adjuntos (capturas) del widget. Reusa la normalización de imágenes del chat de soporte —
 * `normalizeImageToAttachment` ya resuelve resize a 1200px + recompresión JPEG, con passthrough
 * para GIF y fallback a dataURI si el navegador no tiene `toBlob`.
 *
 * Se importa en vez de copiarse: duplicarlo garantizaría que un día se arreglen distinto. Cuando
 * el widget esté estable conviene mover ese helper a `src/utils/`, pero no ahora — tocaría una
 * feature en vivo por estética.
 */
import {normalizeImageToAttachment, previewUrlFor} from '../support_chat/supportChatImage';

export const MAX_FILES = 5;
export const MAX_BYTES_PER_FILE = 10 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 15 * 1024 * 1024;
export const ACCEPTED_TYPES = 'image/png,image/jpeg,image/gif,image/webp';

let idCounter = 0;

function nextId() {
    idCounter += 1;
    return `fb-att-${idCounter}`;
}

/**
 * Agrega archivos a la lista actual respetando los límites.
 *
 * @returns {Promise<{added: Array, rejected: Array<{name: string, reasonKey: string}>}>}
 *
 * El rechazo NUNCA es silencioso: si el usuario arrastra 8 imágenes con un tope de 5, adjuntamos 5
 * y le decimos por nombre cuáles quedaron afuera y por qué. Un dropzone que se come archivos sin
 * avisar hace que el usuario crea que mandó algo que no mandó.
 */
export async function addFiles(current, fileList, {audioBytes = 0} = {}) {
    const files = Array.from(fileList || []);
    const added = [];
    const rejected = [];

    let slots = MAX_FILES - current.length;
    let totalBytes = current.reduce((sum, a) => sum + (a.bytes || 0), 0) + audioBytes;

    for (const file of files) {
        if (!file.type || !file.type.startsWith('image/')) {
            rejected.push({name: file.name, reasonKey: 'feedback.attach.rejected_type'});
            continue;
        }
        if (slots <= 0) {
            rejected.push({name: file.name, reasonKey: 'feedback.attach.rejected_limit'});
            continue;
        }
        if (file.size > MAX_BYTES_PER_FILE) {
            rejected.push({name: file.name, reasonKey: 'feedback.attach.rejected_size'});
            continue;
        }

        let normalized;
        try {
            normalized = await normalizeImageToAttachment(file);
        } catch (_) {
            rejected.push({name: file.name, reasonKey: 'feedback.attach.rejected_type'});
            continue;
        }
        if (!normalized || !normalized.blob) {
            rejected.push({name: file.name, reasonKey: 'feedback.attach.rejected_type'});
            continue;
        }

        const bytes = normalized.blob.size || 0;
        if (totalBytes + bytes > MAX_TOTAL_BYTES) {
            rejected.push({name: file.name, reasonKey: 'feedback.attach.rejected_size'});
            continue;
        }

        totalBytes += bytes;
        slots -= 1;
        added.push({
            // El id se genera acá y es lo que se usa como `key` de React. NUNCA el índice: quitar
            // por índice re-mapea la lista y React reusa el DOM equivocado — el thumbnail que ves
            // deja de ser el archivo que se manda.
            id: nextId(),
            blob: normalized.blob,
            filename: normalized.filename || file.name,
            name: file.name,
            bytes,
            previewUrl: previewUrlFor(normalized),
        });
    }

    return {added, rejected};
}

/** Revoca el objectURL de un adjunto. La propiedad de esos URL es del service, no del componente. */
export function revokeAttachment(attachment) {
    if (attachment && attachment.previewUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) {
        try {
            URL.revokeObjectURL(attachment.previewUrl);
        } catch (_) {
            /* no-op */
        }
    }
}

export function revokeAll(attachments) {
    (attachments || []).forEach(revokeAttachment);
}

/** Sólo las imágenes de un `DataTransfer` / `FileList`. */
export function imageFilesFrom(list) {
    return Array.from(list || []).filter((f) => f && f.type && f.type.startsWith('image/'));
}
